<template>
  <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
    <!-- Event color accent -->
    <div class="h-1" :class="accentClass"></div>

    <div class="px-4 sm:px-6 pt-3.5 sm:pt-4">
      <!-- Breadcrumb -->
      <nav class="flex items-center gap-1 min-w-0 mb-3 text-xs text-gray-400 dark:text-gray-500" aria-label="Breadcrumb">
        <router-link to="/events" data-turbo-prefetch="false"
          class="inline-flex items-center gap-1 font-medium hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path v-for="d in icons.chevronLeft" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
          </svg>
          Events
        </router-link>
        <svg class="w-3 h-3 flex-shrink-0 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path v-for="d in icons.chevronRight" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
        </svg>
        <span class="truncate text-gray-600 dark:text-gray-300">{{ event.name }}</span>
      </nav>

      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <!-- Identity -->
        <div class="flex items-start gap-3.5 min-w-0">
          <div data-test="event-tile"
            class="w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0 rounded-xl flex items-center justify-center text-white text-lg font-bold shadow-sm ring-4 ring-gray-50 dark:ring-gray-800/60"
            :class="accentClass">
            {{ initial }}
          </div>

          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h1 class="max-w-full truncate text-lg sm:text-xl font-bold tracking-tight leading-tight text-gray-900 dark:text-white">
                {{ event.name }}
              </h1>

              <span data-test="status-pill"
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                :class="event.active
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'">
                <span class="w-1.5 h-1.5 rounded-full" :class="event.active ? 'bg-emerald-500' : 'bg-gray-400'"></span>
                {{ event.active ? 'Active' : 'Inactive' }}
              </span>

              <button type="button" data-test="visibility-pill" @click="openPrivacy"
                title="Change privacy"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold transition-colors"
                :class="event.is_public
                  ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:hover:bg-amber-900/40'">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path v-for="d in (event.is_public ? icons.globe : icons.lock)" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
                </svg>
                {{ event.is_public ? 'Public' : 'Private' }}
              </button>
            </div>

            <ul class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <li v-for="item in details" :key="item.key" :data-test="`detail-${item.key}`" :title="item.title"
                class="inline-flex items-center gap-1.5 min-w-0">
                <svg class="w-3.5 h-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                  <path v-for="d in item.icon" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
                </svg>
                <span class="truncate max-w-[16rem]">{{ item.label }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <button type="button" data-test="copy-link" @click="copyLink" :aria-label="copied ? 'Link copied' : 'Copy link'"
            class="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-lg border shadow-sm transition-colors"
            :class="copied
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400'
              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path v-for="d in (copied ? icons.check : icons.link)" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
            </svg>
            <span class="hidden sm:inline">{{ copied ? 'Copied' : 'Copy link' }}</span>
          </button>

          <a data-test="view-page" :href="event.absolute_url" target="_blank" rel="noopener" aria-label="View public page"
            class="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path v-for="d in icons.external" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
            </svg>
            <span class="hidden sm:inline">View page</span>
          </a>

          <button type="button" data-test="edit-event" @click="goEdit"
            class="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 h-9 px-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path v-for="d in icons.pencil" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
            </svg>
            Edit
          </button>

          <div class="relative" ref="menuRef">
            <button type="button" data-test="more-actions" @click="menuOpen = !menuOpen"
              aria-label="More actions" aria-haspopup="menu" :aria-expanded="menuOpen"
              class="inline-flex items-center justify-center w-9 h-9 rounded-lg border shadow-sm transition-colors"
              :class="menuOpen
                ? 'border-gray-300 bg-gray-100 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path v-for="d in icons.ellipsis" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
              </svg>
            </button>

            <Transition
              enter-active-class="transition duration-150 ease-out" enter-from-class="opacity-0 -translate-y-1 scale-95"
              leave-active-class="transition duration-100 ease-in" leave-to-class="opacity-0 -translate-y-1 scale-95">
              <div v-if="menuOpen" role="menu" data-test="more-menu"
                class="absolute right-0 top-full mt-1.5 z-30 w-64 origin-top-right py-1.5 text-sm bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 shadow-lg">
                <button type="button" role="menuitem" data-test="duplicate-event" @click="duplicateEvent" :disabled="duplicating"
                  class="w-full flex items-start gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60">
                  <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path v-for="d in icons.duplicate" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
                  </svg>
                  <span class="min-w-0">
                    <span class="block font-medium text-gray-800 dark:text-gray-100">{{ duplicating ? 'Duplicating' : 'Duplicate' }}</span>
                    <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">Create a copy with the same settings</span>
                  </span>
                </button>

                <button type="button" role="menuitem" data-test="privacy-event" @click="openPrivacy"
                  class="w-full flex items-start gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path v-for="d in icons.lock" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
                  </svg>
                  <span class="min-w-0">
                    <span class="block font-medium text-gray-800 dark:text-gray-100">Privacy</span>
                    <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">Currently {{ event.is_public ? 'public' : 'private' }}</span>
                  </span>
                </button>

                <div class="my-1.5 border-t border-gray-100 dark:border-gray-700"></div>

                <button type="button" role="menuitemcheckbox" :aria-checked="event.active" data-test="toggle-active" @click="toggleActive"
                  class="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <svg class="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path v-for="d in icons.power" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
                  </svg>
                  <span class="flex-1 min-w-0">
                    <span class="block font-medium text-gray-800 dark:text-gray-100">Accepting bookings</span>
                    <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ event.active ? 'Turn off to pause this event' : 'Turn on to take bookings again' }}</span>
                  </span>
                  <span class="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors"
                    :class="event.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'">
                    <span class="inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform"
                      :class="event.active ? 'translate-x-4' : 'translate-x-0.5'"></span>
                  </span>
                </button>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <nav class="mt-3 sm:mt-4 px-2 sm:px-4" aria-label="Event sections">
      <ul class="flex overflow-x-auto no-scrollbar">
        <li v-for="tab in tabs" :key="tab.key" class="flex-shrink-0">
          <router-link :to="tab.path" data-turbo="false" data-turbo-prefetch="false" :data-test="`tab-${tab.key}`"
            :aria-current="currentTab === tab.key ? 'page' : undefined"
            class="group inline-flex items-center gap-2 px-3 py-3 text-sm whitespace-nowrap border-b-2 transition-colors"
            :class="currentTab === tab.key
              ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 font-semibold'
              : 'border-transparent text-gray-500 dark:text-gray-400 font-medium hover:text-gray-900 hover:border-gray-300 dark:hover:text-gray-100 dark:hover:border-gray-600'">
            <svg class="w-4 h-4 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"
              :class="currentTab === tab.key ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'">
              <path v-for="d in icons[tab.key]" :key="d" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
            </svg>
            <span data-test="tab-label">{{ tab.label }}</span>
            <span v-if="tab.soon" data-test="soon-badge"
              class="px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500">
              Soon
            </span>
          </router-link>
        </li>
      </ul>
    </nav>

    <EventPrivacyModal :open="privacyOpen" :is-public="!!event.is_public" :saving="privacySaving"
      @close="privacyOpen = false" @save="savePrivacy" />
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useConstants } from 'composables/useConstants.js'
import { useRequest } from 'composables/useRequest.js'
import { useEventsStore } from 'stores/events.js'
import EventPrivacyModal from 'controllers/vue_components/events/EventPrivacyModal'
import { EVENT_ICONS, locationIcon } from 'controllers/vue_components/events/eventIcons.js'

const props = defineProps({
  event: { type: Object, required: true },
  currentTab: { type: String, required: true },
})

// The header never mutates the event prop. EventLayout owns it and applies
// these patches, so every tab sees the change.
const emit = defineEmits(['updated'])

const router = useRouter()
const { I18N, APPLICATION_URL } = useConstants()
// The toast lives in EventLayout so it survives this header unmounting, e.g.
// when a duplicate navigates to the new event and the layout reloads.
const flash = inject('eventFlash', ref(null))
const { axiosRequest } = useRequest(flash)
const eventsStore = useEventsStore()

const icons = EVENT_ICONS

const copied        = ref(false)
const menuOpen      = ref(false)
const menuRef       = ref(null)
const duplicating   = ref(false)
const privacyOpen   = ref(false)
const privacySaving = ref(false)

const tabs = computed(() => [
  { key: 'bookings',    label: I18N.application.index.bookings },
  { key: 'attendees',   label: I18N.website_content.app_nav.attendees },
  { key: 'analytics',   label: I18N.website_content.app_nav.analytics,              soon: true },
  { key: 'qr_code',     label: I18N.website_content.account_settings.qr_code.title, soon: true },
  { key: 'embed',       label: I18N.website_content.account_settings.embed.title,   soon: true },
  { key: 'link_button', label: I18N.website_content.account_settings.link_button,   soon: true },
].map((tab) => ({ ...tab, path: `/events/${props.event.id}/${tab.key}` })))

const accentClass = computed(() =>
  props.event.active ? props.event.color : 'bg-gray-300 dark:bg-gray-600'
)

// Skips leading symbols so "[CLONE] Intro call" still gets a letter.
const initial = computed(() => {
  const match = (props.event.name || '').match(/[\p{L}\p{N}]/u)
  return match ? match[0].toUpperCase() : '?'
})

// The API sends padding already localised ("0 Minutes"), so a zero buffer is
// detected against the locale string with a numeric fallback.
function hasBuffer(padding) {
  if (!padding) return false
  const zero = I18N?.website_content?.account_settings?.['0_minutes']
  return padding !== zero && !/^0(\D|$)/.test(padding.trim())
}

const details = computed(() => {
  const event = props.event
  const items = []
  if (event.slot_duration) {
    items.push({ key: 'duration', icon: icons.clock, label: event.slot_duration, title: 'Duration' })
  }
  if (hasBuffer(event.padding_duration)) {
    items.push({ key: 'buffer', icon: icons.buffer, label: `${event.padding_duration} buffer`, title: 'Break between bookings' })
  }
  if (event.location_name && event.location_name !== 'None') {
    items.push({ key: 'location', icon: locationIcon(event.location_name), label: event.location_name, title: 'Location' })
  }
  if (event.connected_calendar_email) {
    items.push({ key: 'calendar', icon: icons.calendar, label: event.connected_calendar_email, title: 'Bookings are added to this calendar' })
  }
  if (event.created_at) {
    items.push({ key: 'created', icon: icons.sparkles, label: `Created ${event.created_at}`, title: event.formated_created_at_for_host || event.created_at })
  }
  return items
})

const publicUrl = computed(() => `${APPLICATION_URL}${props.event.absolute_url || ''}`)

async function copyLink() {
  try {
    await navigator.clipboard.writeText(publicUrl.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    flash.value?.show('Could not copy the link.', 'alert')
  }
}

function goEdit() {
  router.push(`/events/${props.event.id}/edit`)
}

async function duplicateEvent() {
  if (duplicating.value) return
  duplicating.value = true
  // `true` lets the API's "event cloned" message show as the toast.
  const res = await axiosRequest('POST', `/api/v1/events/${props.event.id}/clone`, {}, true)
  duplicating.value = false
  menuOpen.value = false
  if (!res.js_error && res.response.success) {
    eventsStore.addEvent(res.response.data)
    eventsStore.invalidate()
    router.push(`/events/${res.response.data.id}/${props.currentTab}`)
  }
}

function openPrivacy() {
  menuOpen.value = false
  privacyOpen.value = true
}

async function savePrivacy(isPublic) {
  privacySaving.value = true
  const res = await axiosRequest('PUT', `/api/v1/events/${props.event.id}`, { event: { is_public: isPublic } }, false)
  privacySaving.value = false
  if (!res.js_error && res.response.success) {
    emit('updated', { is_public: isPublic })
    eventsStore.updateEvent(props.event.id, { is_public: isPublic, tempPublic: isPublic })
    privacyOpen.value = false
    flash.value?.show(isPublic ? 'Event set to public' : 'Event set to private', 'notice')
  } else {
    flash.value?.show('Failed to update privacy', 'alert')
  }
}

// Optimistic, like the toggle on /events: flip now, roll back if the API fails.
async function toggleActive() {
  const active = !props.event.active
  emit('updated', { active })
  const res = await axiosRequest('PUT', `/api/v1/events/${props.event.id}`, { event: { active } }, false)
  if (!res.js_error && res.response.success) {
    eventsStore.updateEvent(props.event.id, { active })
    const message = active
      ? I18N?.notifications?.events?.event_activated || 'Event activated'
      : I18N?.notifications?.events?.event_deactivated || 'Event deactivated'
    flash.value?.show(message, 'notice')
  } else {
    emit('updated', { active: !active })
    flash.value?.show('Failed to update event', 'alert')
  }
}

function closeMenuOnOutsideClick(e) {
  if (menuOpen.value && menuRef.value && !menuRef.value.contains(e.target)) menuOpen.value = false
}

function closeMenuOnEscape(e) {
  if (e.key === 'Escape') menuOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', closeMenuOnOutsideClick)
  document.addEventListener('keydown', closeMenuOnEscape)
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenuOnOutsideClick)
  document.removeEventListener('keydown', closeMenuOnEscape)
})
</script>
